import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, finalize, Observable, of, share, shareReplay, switchMap, throwError } from 'rxjs';
import { LoginResponse } from './auth.types';
import { UserService } from 'app/modules/users/user.service';
import { User } from 'app/modules/users/user.types';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    private _infligthRefreshTokenRequest$: Observable<LoginResponse> | null = null;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken');
    }

    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/users/forgot-password', { email });
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/users/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<LoginResponse> {
        return this._httpClient.post('api/users/login/password', credentials).pipe(
            switchMap((response: any) => {
                // Store the access and refresh tokens in the local storage
                this.accessToken = response.accessToken;
                this.refreshToken = response.refreshToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    refreshAccessToken(): Observable<LoginResponse> {
        if (!this._infligthRefreshTokenRequest$) {
            this._infligthRefreshTokenRequest$ = this._httpClient.post<LoginResponse>('api/users/refresh', { refreshToken: this.refreshToken })
                .pipe(
                    switchMap((response: LoginResponse) => {
                        // Store the new access and refresh tokens in the local storage
                        this.accessToken = response.accessToken;
                        this.refreshToken = response.refreshToken;
                        // Set the authenticated flag to true
                        this._authenticated = true;
                        // Store the user on the user service
                        this._userService.user = response.user;
                        // Return a new observable with the response
                        return of(response);
                    }),
                    finalize(() => {
                        // Clear the inflight request
                        this._infligthRefreshTokenRequest$ = null;
                    }),
                    shareReplay(1),
                );
        }

        return this._infligthRefreshTokenRequest$;
    }


    /**
     * Sign in using the access token
     */
    getCurrentUser(): Observable<boolean> {
        // Sign in using the token
        return this._httpClient.get<User>('api/users/current-user')
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: User) => {
                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response;

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Set the authenticated flag to false
        this._authenticated = false;
        this._userService.user = null;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/users/register', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.getCurrentUser();
    }
}
